import React, { useEffect, useState } from "react";
import { useValuation } from "../context/ValuationContext";
import { useLiveFormData } from "@/hooks/useLiveFormData";

const ValuationResults: React.FC = () => {
  const { valuationResults, formatCurrency } = useValuation();
  const { combinedData } = useLiveFormData();

  // / Keep the existing valuation logic...
  const [valuation, setValuation] = useState({
    landValue: 0,
    buildingReplacementCost: 0,
    depreciatedValue: 0,
    amenitiesValue: 0,
    totalValue: 0,
    confidenceLevel: 0,
  });

    // Country & currency detection — defaults to UGX, RWF for Rwanda
  const country = combinedData?.country;
  const isRwanda = country === "RW" || country?.toLowerCase() === "rwanda";
  const currency = isRwanda ? "RWF" : "UGX";
  
 const formatLocalCurrency = (value: number) => {
    if (!value && value !== 0) return `${currency} 0`;
    return `${currency} ${Number(value).toLocaleString()}`;
  };

  useEffect(() => {
    if (combinedData) {
      const landArea = Number(combinedData.landArea) || 0;
      const landRate = Number(combinedData.landRate) || 0;
      const builtArea = Number(combinedData.mainHouseArea) || 0;
      const yearBuilt = combinedData.yearOfConstruction
        ? Number(combinedData.yearOfConstruction)
        : new Date().getFullYear();

      const landValue = landArea * landRate;
      const totalDepreciation = Math.min(
        0.8,
        combinedData.effectiveAge * combinedData.depreciationRate,
      );
      const buildingReplacementCost =
        Number(combinedData.mainHouseArea) * combinedData.mainHouseRate;

      let amenitiesValue = 0;
      if (combinedData.annexHouseArea)
        amenitiesValue += combinedData.annexHouseArea * combinedData.annexRate;
      if (combinedData.fenceLength)
        amenitiesValue += combinedData.fenceLength * combinedData.fenceRate;
      if (combinedData.gardenArea)
        amenitiesValue += combinedData.gardenArea * combinedData.gardenRate;
      if (combinedData.retainingWallsLength)
        amenitiesValue +=
          combinedData.retainingWallsLength * combinedData.retainingWallRate;
      if (combinedData.entranceGateLength)
        amenitiesValue +=
          combinedData.entranceGateLength * combinedData.entranceGateRate;
      if (combinedData.pavedArea)
        amenitiesValue += combinedData.pavedArea * combinedData.pavedAreaRate;
      if (combinedData.septicTankNumber)
        amenitiesValue +=
          combinedData.septicTankNumber * combinedData.septicTankValue;
      if (combinedData.verandahArea)
        amenitiesValue += combinedData.verandahArea * combinedData.verandahRate;
      const depreciatedValue = buildingReplacementCost + amenitiesValue;
      const totalValue = landValue + depreciatedValue;
      const requiredFields = [
        combinedData.upi,
        combinedData.ownerIdNumber,
        combinedData.province,
        combinedData.district,
        landArea,
        landRate,
        builtArea,
        yearBuilt,
        combinedData.roofType,
        combinedData.wallMaterial,
      ];
      const completedFields = requiredFields.filter((field) => {
        if (typeof field === "number") return field > 0;
        return field !== undefined && field !== null && field !== "";
      }).length;

      const confidenceLevel = Math.round(
        (completedFields / requiredFields.length) * 100,
      );
      const newValuation = {
        landValue,
        buildingReplacementCost,
        depreciatedValue,
        amenitiesValue,
        totalValue,
        confidenceLevel,
      };
      setValuation((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(newValuation)) return prev;
        return newValuation;
      });
    }
  }, [combinedData]);

  return (
    <section className="mb-12 p-4 md:p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">
        Estimated Value
      </h2>
      <div className="flex flex-col gap-4">
        <div className="bg-gray-50 p-4 rounded-lg shadow-inner">
          <h3 className="text-sm md:text-lg font-semibold text-gray-500">
            Open Market Value
          </h3>
          <p className="text-lg md:text-xl font-bold my-2 md:my-3 text-gray-900 break-words overflow-hidden">
            {formatLocalCurrency(valuation.totalValue)}
          </p>
          <p className="text-xs text-gray-500">
            Estimated amount for which the property should exchange between a
            willing buyer and seller
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg shadow-inner">
          <h3 className="text-sm md:text-lg font-semibold text-gray-500">
            Insurance Value
          </h3>
          <p className="text-lg md:text-xl font-bold my-2 md:my-3 text-gray-900 break-words overflow-hidden">
            {formatLocalCurrency(valuation.depreciatedValue)}
          </p>
          <p className="text-xs text-gray-500">
            Value for insurance purposes, typically covering replacement cost
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg shadow-inner">
          <h3 className="text-sm md:text-lg font-semibold text-gray-500">
            Forced Sale Value
          </h3>
          <p className="text-lg md:text-xl font-bold my-2 md:my-3 text-gray-900 break-words overflow-hidden">
            {formatLocalCurrency(valuation.totalValue * 0.7)}
          </p>
          <p className="text-xs text-gray-500">
            Estimated realization price in forced sale circumstances
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg shadow-inner">
          <h3 className="text-sm md:text-lg font-semibold text-gray-500">
            Improvements Value
          </h3>
          <p className="text-lg md:text-xl font-bold my-2 md:my-3 text-gray-900 break-words overflow-hidden">
            {formatLocalCurrency(valuation.amenitiesValue)}
          </p>
          <p className="text-xs text-gray-500">
            Total value of all buildings and improvements
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg shadow-inner">
          <h3 className="text-sm md:text-lg font-semibold text-gray-500">
            Land Value
          </h3>
          <p className="text-lg md:text-xl font-bold my-2 md:my-3 text-gray-900 break-words overflow-hidden">
           {formatLocalCurrency(valuation.landValue)}
          </p>
          <p className="text-xs text-gray-500">
            Value of the land based on area and location
          </p>
        </div>
      </div>
    </section>
  );
};

export default ValuationResults;
